import { Bitmap } from "./bitmap";
import type { SceneLight } from "./rooms";

export type Mood = "day" | "golden" | "night" | "dusk";

export const MOODS: Record<Mood, { ambient: [number, number, number]; shaft: [number, number, number]; lamp: number; shaftOn: number }> = {
  day: { ambient: [0.78, 0.78, 0.84], shaft: [0.32, 0.24, 0.15], lamp: 0.7, shaftOn: 1 },
  golden: { ambient: [0.66, 0.6, 0.62], shaft: [0.5, 0.3, 0.14], lamp: 0.85, shaftOn: 1.2 },
  night: { ambient: [0.36, 0.4, 0.58], shaft: [0.06, 0.08, 0.14], lamp: 1.1, shaftOn: 0.2 },
  /** the close-up mock: a blue room, a warm pool of lamplight on the table, a sunset shaft */
  dusk: { ambient: [0.4, 0.44, 0.62], shaft: [0.5, 0.3, 0.2], lamp: 1.0, shaftOn: 0.85 },
};

export interface LightMaps {
  /** multiply layer: 0..255 per channel (light below 1.0) */
  shade: Bitmap;
  /** additive layer: the part of the light above 1.0, plus window shafts, as premultiplied glow */
  glow: Bitmap;
}

export interface Shaft {
  /** polygon in world pixels */
  pts: [number, number][];
}

/**
 * Builds the HD-2D light map for a scene (same maths as the Python mocks):
 * ambient + lamp pools (elliptical falloff) + soft window shafts.
 */
export function lightMaps(w: number, h: number, lights: SceneLight[], shafts: Shaft[], mood: Mood = "day"): LightMaps {
  const m = MOODS[mood];
  const L = new Float32Array(w * h * 3);
  for (let i = 0; i < w * h; i++) {
    L[i * 3] = m.ambient[0];
    L[i * 3 + 1] = m.ambient[1];
    L[i * 3 + 2] = m.ambient[2];
  }
  for (const l of lights) {
    const rx = l.radius * 1.15 * 2;
    const ry = l.radius * 1.15;
    const x0 = Math.max(0, Math.floor(l.px - rx));
    const x1 = Math.min(w - 1, Math.ceil(l.px + rx));
    const y0 = Math.max(0, Math.floor(l.py - ry));
    const y1 = Math.min(h - 1, Math.ceil(l.py + ry * 1.6));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const dx = (x - l.px) / rx;
      const dy = (y - l.py - ry * 0.3) / ry;
      const v = 1 - (dx * dx + dy * dy);
      if (v <= 0) continue;
      const k = Math.pow(v, 1.3) * l.strength * m.lamp * 0.7;
      const i = (y * w + x) * 3;
      L[i] += l.color[0] * k;
      L[i + 1] += l.color[1] * k;
      L[i + 2] += l.color[2] * k;
    }
  }
  // window shafts: rasterise, blur cheaply (box blur twice), add warm light
  if (shafts.length && m.shaftOn > 0) {
    const mask = new Bitmap(w, h);
    for (const s of shafts) mask.poly(s.pts, [255, 255, 255]);
    const a = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) a[i] = mask.data[i * 4 + 3] / 255;
    const blurred = boxBlur(boxBlur(a, w, h, 6), w, h, 6);
    for (let i = 0; i < w * h; i++) {
      const s = blurred[i] * m.shaftOn;
      L[i * 3] += s * m.shaft[0];
      L[i * 3 + 1] += s * m.shaft[1];
      L[i * 3 + 2] += s * m.shaft[2];
    }
  }
  const shade = new Bitmap(w, h);
  const glow = new Bitmap(w, h);
  for (let i = 0; i < w * h; i++) {
    const r = L[i * 3];
    const g = L[i * 3 + 1];
    const b = L[i * 3 + 2];
    shade.data[i * 4] = Math.min(255, r * 255);
    shade.data[i * 4 + 1] = Math.min(255, g * 255);
    shade.data[i * 4 + 2] = Math.min(255, b * 255);
    shade.data[i * 4 + 3] = 255;
    const er = Math.max(0, r - 1);
    const eg = Math.max(0, g - 1);
    const eb = Math.max(0, b - 1);
    const ea = Math.min(1, Math.max(er, eg, eb) * 1.6);
    glow.data[i * 4] = Math.min(255, er * 160);
    glow.data[i * 4 + 1] = Math.min(255, eg * 160);
    glow.data[i * 4 + 2] = Math.min(255, eb * 160);
    glow.data[i * 4 + 3] = ea * 255;
  }
  return { shade, glow };
}

function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const n = r * 2 + 1;
  for (let y = 0; y < h; y++) {
    let acc = 0;
    for (let x = -r; x <= r; x++) acc += src[y * w + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = acc / n;
      acc += src[y * w + Math.min(w - 1, x + r + 1)] - src[y * w + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / n;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
  return out;
}

/** Window shafts falling from the back and left walls into the room (like the mock's sunbeams). */
export function windowShafts(scene: { P: (x: number, y: number, z?: number) => [number, number]; windows: { ux: number; uy: number; plane: "x" | "y"; width: number }[] }): Shaft[] {
  return scene.windows.map((wd) => {
    if (wd.plane === "y") {
      const pts: [number, number][] = [scene.P(wd.ux, 0, 24), scene.P(wd.ux + wd.width, 0, 24), scene.P(wd.ux + wd.width + 6, 16, 0), scene.P(wd.ux + 6, 16, 0)];
      return { pts };
    }
    const pts: [number, number][] = [scene.P(0, wd.uy, 23), scene.P(0, wd.uy + wd.width, 23), scene.P(14, wd.uy + wd.width + 2, 0), scene.P(14, wd.uy + 2, 0)];
    return { pts };
  });
}

/** Composite base × light + glow on the CPU (for QA stills and tests; the browser does this on the GPU). */
export function applyLight(img: Bitmap, maps: LightMaps): Bitmap {
  const out = new Bitmap(img.w, img.h);
  for (let i = 0; i < img.w * img.h; i++) {
    for (let k = 0; k < 3; k++) {
      const v = (img.data[i * 4 + k] * maps.shade.data[i * 4 + k]) / 255 + (maps.glow.data[i * 4 + k] * maps.glow.data[i * 4 + 3]) / 255;
      out.data[i * 4 + k] = Math.min(255, v);
    }
    out.data[i * 4 + 3] = 255;
  }
  return out;
}
